import { useTsController } from "@ts-react/form";
import { type ComponentType, useId } from "react";
import { useFormContext } from "react-hook-form";
import { ListItem, XStack, YGroup } from "tamagui";
import type { IconProps } from "@tamagui/helpers-icon";
import { ChevronRight, Globe, Headset, HelpCircle, ListChecks, Receipt, Search, ShieldCheck, XCircle } from "@tamagui/lucide-icons";
import { textPrimaryHex } from "../../themes/legacy/colors-granted";
import { WithSeparator } from "../Separator";
import { Paragraph } from "../Typography";
import { Fieldset } from "./Fieldset";
import { FormError } from "./FormError";

// TODO make this more flexible, allow for images and emojis so we can create a
// shared util with other form fields, will be needed for backend dynamic flow
// fields
type IconMapping = {
  [key: string]: ComponentType<IconProps>;
};
const iconMapping: IconMapping = {
  Receipt: Receipt,
  ListChecks: ListChecks,
  Globe: Globe,
  Search: Search,
  ShieldCheck: ShieldCheck,
  XCircle: XCircle,
  Headset: Headset,
  HelpCircle: HelpCircle
};
type IconLookupKey = keyof typeof iconMapping;
export type ListSelectOption = {
  id: string;
  icon?: IconLookupKey;
  label: string;
};
export type ListSelectItemProps = {
  id: string;
  icon: IconLookupKey;
  label: string;
  disabled?: boolean;
  onPress?: () => void;
};
export const ListSelectItem = ({
  id,
  icon,
  label,
  disabled,
  onPress
}: ListSelectItemProps) => {
  let Icon: ComponentType<IconProps> | undefined;
  if (icon && iconMapping[icon]) {
    Icon = iconMapping[icon];
  }
  return <YGroup.Item data-sentry-element="YGroup.Item" data-sentry-component="ListSelectItem" data-sentry-source-file="ListSelectField.tsx">
      <ListItem id={id} hoverStyle={{
      opacity: 0.85
    }} pressStyle={{
      opacity: 0.7
    }} title={<Paragraph color={textPrimaryHex}>{label}</Paragraph>} icon={Icon ? <Icon size="$1.5" color={textPrimaryHex} /> : undefined} iconAfter={<ChevronRight size="$1.5" color="$textSecondary" />} disabled={disabled} onPress={onPress} paddingVertical="$5" data-sentry-element="ListItem" data-sentry-source-file="ListSelectField.tsx"></ListItem>
    </YGroup.Item>;
};
export type ListSelectFieldProps = {
  native?: boolean;
};
export const ListSelectField = ({
  options,
  onSelect
}: {
  options: ListSelectOption[];
  onSelect?: () => Promise<void>;
} & Pick<ListSelectFieldProps, "native">) => {
  const {
    field,
    error,
    formState: {
      isSubmitting
    }
  } = useTsController<string>();
  const {
    resetField
  } = useFormContext();
  const id = useId();
  const disabled = isSubmitting;

  // This function does not currently support de-selecting a card
  // It is intended to be used when we want to fire an event when a
  // card is selected and then submit.
  async function handleSelect(option: string) {
    // marks the field as dirty
    resetField(field.name);
    field.onChange(option);
    await onSelect?.();
  }
  return <Fieldset data-sentry-element="Fieldset" data-sentry-component="ListSelectField" data-sentry-source-file="ListSelectField.tsx">
      <XStack flex={1} alignContent="center" justifyContent="center" data-sentry-element="XStack" data-sentry-source-file="ListSelectField.tsx">
        <YGroup width="100%" size="$5" id={id} data-sentry-element="YGroup" data-sentry-source-file="ListSelectField.tsx">
          <WithSeparator data-sentry-element="WithSeparator" data-sentry-source-file="ListSelectField.tsx">
            {options.map(option => {
            return <ListSelectItem key={option.id} disabled={disabled} label={option.label} id={option.id} icon={option.icon as IconLookupKey} onPress={() => {
              void handleSelect(option.id);
            }} />;
          })}
          </WithSeparator>
        </YGroup>
      </XStack>
      <FormError errorMessage={error?.errorMessage} data-sentry-element="FormError" data-sentry-source-file="ListSelectField.tsx" />
    </Fieldset>;
};